import { getOwnProperty, objectMapValues } from "@supersig/utils";
import { MigrationFailureReason, migrate, migrateRecord } from "./migrate.mjs";
/** @public */ export class SWStoreSchemaImpl {
    types;
    options;
    static create(// HACK: making this param work with RecordType is an enormous pain
    // let's just settle for making sure each typeName has a corresponding RecordType
    // and accept that this function won't be able to infer the record type from it's arguments
    types, options) {
        return new SWStoreSchemaImpl(types, options ?? {});
    }
    constructor(types, options){
        this.types = types;
        this.options = options;
    }
    get currentStoreVersion() {
        return this.options.snapshotMigrations?.currentVersion ?? 0;
    }
    validateRecord(store, record, phase, recordBefore) {
        try {
            const recordType = getOwnProperty(this.types, record.typeName);
            if (!recordType) {
                throw new Error(`Missing definition for record type ${record.typeName}`);
            }
            return recordType.validate(record);
        } catch (error) {
            if (this.options.onValidationFailure) {
                return this.options.onValidationFailure({
                    store,
                    record,
                    phase,
                    recordBefore,
                    error
                });
            } else {
                throw error;
            }
        }
    }
    migratePersistedRecord(record, persistedSchema, direction = "up") {
        const ourType = getOwnProperty(this.types, record.typeName);
        const persistedType = persistedSchema.recordVersions[record.typeName];
        if (!persistedType || !ourType) {
            return {
                type: "error",
                reason: MigrationFailureReason.UnknownType
            };
        }
        const ourVersion = ourType.migrations.currentVersion;
        const persistedVersion = persistedType.version;
        if (ourVersion !== persistedVersion) {
            const result = direction === "up" ? migrateRecord({
                record,
                migrations: ourType.migrations,
                fromVersion: persistedVersion,
                toVersion: ourVersion
            }) : migrateRecord({
                record,
                migrations: ourType.migrations,
                fromVersion: ourVersion,
                toVersion: persistedVersion
            });
            if (result.type === "error") {
                return result;
            }
            record = result.value;
        }
        if (!ourType.migrations.subTypeKey) {
            return {
                type: "success",
                value: record
            };
        }
        // we've handled the main version migration, now we need to handle subtypes
        // subtypes are used by shape and asset types to migrate the props shape, which is configurable
        // by library consumers.
        const ourSubTypeMigrations = ourType.migrations.subTypeMigrations?.[record[ourType.migrations.subTypeKey]];
        const persistedSubTypeVersion = "subTypeVersions" in persistedType ? persistedType.subTypeVersions[record[ourType.migrations.subTypeKey]] : undefined;
        // if ourSubTypeMigrations is undefined then we don't have access to the migrations for this subtype
        // that is almost certainly because we are running on the server and this type was supplied by a 3rd party.
        // It could also be that we are running in a client that is outdated. Either way, we can't migrate this record
        // and we need to let the consumer know so they can handle it.
        if (ourSubTypeMigrations === undefined) {
            return {
                type: "error",
                reason: MigrationFailureReason.UnrecognizedSubtype
            };
        }
        // if the persistedSubTypeVersion is undefined then the record was either created after the schema
        // was persisted, or it was created in a different place to where the schema was persisted.
        // either way we don't know what to do with it safely, so let's return failure.
        if (persistedSubTypeVersion === undefined) {
            return {
                type: "error",
                reason: MigrationFailureReason.IncompatibleSubtype
            };
        }
        const result = direction === "up" ? migrateRecord({
            record,
            migrations: ourSubTypeMigrations,
            fromVersion: persistedSubTypeVersion,
            toVersion: ourSubTypeMigrations.currentVersion
        }) : migrateRecord({
            record,
            migrations: ourSubTypeMigrations,
            fromVersion: ourSubTypeMigrations.currentVersion,
            toVersion: persistedSubTypeVersion
        });
        if (result.type === "error") {
            return result;
        }
        return {
            type: "success",
            value: result.value
        };
    }
    migrateStoreSnapshot(snapshot) {
        let { store } = snapshot;
        const migrations = this.options.snapshotMigrations;
        if (!migrations) {
            return {
                type: "success",
                value: store
            };
        }
        // apply store migrations first
        const ourStoreVersion = migrations.currentVersion;
        const persistedStoreVersion = snapshot.schema.storeVersion ?? 0;
        if (ourStoreVersion < persistedStoreVersion) {
            return {
                type: "error",
                reason: MigrationFailureReason.TargetVersionTooOld
            };
        }
        if (ourStoreVersion > persistedStoreVersion) {
            const result = migrate({
                value: store,
                migrations,
                fromVersion: persistedStoreVersion,
                toVersion: ourStoreVersion
            });
            if (result.type === "error") {
                return result;
            }
            store = result.value;
        }
        const updated = [];
        for (const r of objectMapValues(store)){
            const result = this.migratePersistedRecord(r, snapshot.schema);
            if (result.type === "error") {
                return result;
            } else if (result.value && result.value !== r) {
                updated.push(result.value);
            }
        }
        if (updated.length) {
            store = {
                ...store
            };
            for (const r of updated){
                store[r.id] = r;
            }
        }
        return {
            type: "success",
            value: store
        };
    }
    /** @internal */ createIntegrityChecker(store) {
        return this.options.createIntegrityChecker?.(store) ?? undefined;
    }
    serialize() {
        return {
            schemaVersion: 1,
            storeVersion: this.options.snapshotMigrations?.currentVersion ?? 0,
            recordVersions: Object.fromEntries(objectMapValues(this.types).map((type)=>[
                    type.typeName,
                    type.migrations.subTypeKey && type.migrations.subTypeMigrations ? {
                        version: type.migrations.currentVersion,
                        subTypeKey: type.migrations.subTypeKey,
                        subTypeVersions: type.migrations.subTypeMigrations ? Object.fromEntries(Object.entries(type.migrations.subTypeMigrations).map(([k, v])=>[
                                k,
                                v.currentVersion
                            ])) : undefined
                    } : {
                        version: type.migrations.currentVersion
                    }
                ]))
        };
    }
    serializeEarliestVersion() {
        return {
            schemaVersion: 1,
            storeVersion: this.options.snapshotMigrations?.firstVersion ?? 0,
            recordVersions: Object.fromEntries(objectMapValues(this.types).map((type)=>[
                    type.typeName,
                    type.migrations.subTypeKey && type.migrations.subTypeMigrations ? {
                        version: type.migrations.firstVersion,
                        subTypeKey: type.migrations.subTypeKey,
                        subTypeVersions: type.migrations.subTypeMigrations ? Object.fromEntries(Object.entries(type.migrations.subTypeMigrations).map(([k, v])=>[
                                k,
                                v.firstVersion
                            ])) : undefined
                    } : {
                        version: type.migrations.firstVersion
                    }
                ]))
        };
    }
}
